import { API_URL } from '@/constants/config';

/**
 * The base URL every module builds request paths from, without the trailing slash — callers write
 * `${API_BASE_URL}/master-data/x`.
 *
 * Derived from `@/constants/config`'s `API_URL` rather than re-read from `window.env`. It used to
 * be the latter, which meant it had no fallback when `window.env` was absent and resolved to
 * `undefined`, sending 133 modules' requests to `undefined/...` — while `config.ts`, two files
 * away, had a fallback for exactly that case. It also read bare `window`, which is why any
 * `node`-environment test importing this module failed at collection.
 */
export const API_BASE_URL = API_URL.replace(/\/$/, '');

export { API_URL };

// Guarded so modules importing constants stay loadable in non-browser
// environments (node-env unit tests).
const windowEnv = typeof window !== 'undefined' ? window.env : undefined;

export const CLIENT_ID = windowEnv?.CLIENT_ID;

export const CLIENT_SECRET = windowEnv?.CLIENT_SECRET;

export const RECAPTCHA_SITEKEY = windowEnv?.RECAPTCHA_SITEKEY ?? '';

export const S3_URL = windowEnv?.S3_URL || 'https://vouch-av.s3.ap-southeast-1.amazonaws.com/';

export const CACHE_TIMES = {
  STALE_TIME: 5 * 60 * 1000, // 5 minutes
  GC_TIME: 10 * 60 * 1000, // 10 minutes
} as const;

const PAGE_SIZE_OPTIONS = [10, 20] as const;

export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 10,
  // Derived from the offered options so the cap and the dropdown can't drift
  // apart — adding an option raises the cap, and nothing else has to change.
  MAX_PAGE_SIZE: Math.max(...PAGE_SIZE_OPTIONS),
  PAGE_SIZE_OPTIONS,
} as const;

/**
 * Version stamped onto every store that persists `pagination`. Bump it when a
 * persisted shape stops being safe to reuse; the matching `migrate` then runs
 * once per browser. Currently 1: builds before this offered page sizes up to
 * 50, so a stale `limit` can still be sitting in localStorage.
 */
export const PAGINATION_PERSIST_VERSION = 1;

/**
 * Clamp a page size into the offered range. Covers the two paths the dropdown
 * cannot police: a `limit` written by an older build, and a hand-edited
 * localStorage value. Anything unusable falls back to the default rather than
 * propagating NaN into a query param.
 */
export const clampPageSize = (limit: unknown): number => {
  const value = typeof limit === 'number' ? limit : Number(limit);
  if (!Number.isFinite(value) || value < 1) return PAGINATION.DEFAULT_PAGE_SIZE;
  return Math.min(Math.trunc(value), PAGINATION.MAX_PAGE_SIZE);
};

/**
 * `persist({ migrate })` helper — clamps `pagination.limit` on a rehydrated
 * state and leaves every other key untouched. Stores with migration work of
 * their own compose it:
 *   `migrate: (state, version) => clampPersistedPageSize(ownMigrate(state, version))`
 */
export const clampPersistedPageSize = <T>(persistedState: T): T => {
  const state = persistedState as { pagination?: { limit?: unknown } } | null | undefined;
  const pagination = state?.pagination;
  if (!pagination || typeof pagination !== 'object') return persistedState;
  return { ...state, pagination: { ...pagination, limit: clampPageSize(pagination.limit) } } as T;
};

// ─── Sticky Columns (shared across assignment-config + staff-management) ──

export const STICKY_COLUMNS = ['rf', 'sc', 'de', 'admin', 'delivery'] as const;
export type StickyColumn = (typeof STICKY_COLUMNS)[number];

export const STICKY_COLUMN_LABELS: Record<StickyColumn, string> = {
  rf: 'RF',
  sc: 'SC',
  de: 'DE',
  admin: 'Admin',
  delivery: 'Delivery',
};

export const NON_MOTOR_COUNTRY_CODE = 'TH';
export const NON_MOTOR_BUSINESS_UNIT = 'FAIRDEE';

export interface NonMotorPlatformProductBase {
  code: string;
  key: string;
  category: string;
  label: string;
  filterLabel: string;
  filterValue: string;
}

export const NON_MOTOR_PLATFORM_PRODUCTS: NonMotorPlatformProductBase[] = [
  {
    code: 'FAIRDEE_TH_TRAVEL',
    key: 'travel',
    category: 'TRAVEL',
    label: 'Travel Insurance',
    filterLabel: 'Travel',
    filterValue: 'TRAVEL',
  },
  {
    code: 'FAIRDEE_TH_PERSONAL_ACCIDENT',
    key: 'pa',
    category: 'MICROHEALTH',
    label: 'Personal Accident',
    filterLabel: 'Personal Accident',
    filterValue: 'PERSONAL_ACCIDENT',
  },
  {
    code: 'FAIRDEE_TH_MOTOR_BIKE',
    key: 'motor_bike',
    category: 'MOTOR',
    label: 'Motor Bike',
    filterLabel: 'Motor Bike',
    filterValue: 'MOTOR_BIKE',
  },
  {
    code: 'FAIRDEE_TH_FIRE',
    key: 'fire',
    category: 'FIRE',
    label: 'Fire Insurance',
    filterLabel: 'Fire',
    filterValue: 'FIRE',
  },
  {
    code: 'FAIRDEE_TH_NON_STANDARD',
    key: 'Non Standard',
    category: 'NON_STANDARD',
    label: 'Non Standard Products',
    filterLabel: 'Non Standard Products',
    filterValue: 'NON_STANDARD',
  },
  {
    code: 'FAIRDEE_TH_CARRIER_LIABILITY',
    key: 'carrier',
    category: 'CARRIER_LIABILITY',
    label: 'Carrier Liability',
    filterLabel: 'Carrier Liability',
    filterValue: 'CARRIER_LIABILITY',
  },
];

/**
 * Canonical bank list for agent bank accounts.
 *
 * The stored value of `bank_details.bank_name` is the KEY of these maps, not the label.
 * Those keys are the contract: vouch-api resolves them to a bank code via
 * BANK_NAME_TO_CODE_MAPPING / BANK_NAME_TO_CODE_MAPPING_FOR_KBANK for commission
 * transfers and payout CSVs, and the agent portal matches on them too. Renaming a key
 * silently breaks a payout, so treat this list as append-only and keep it in sync with
 * the backend mapping.
 */

export const BANK_LIST_EN: Record<string, string> = {
  Kasikornbank: 'Kasikornbank',
  'Siam Commercial Bank': 'Siam Commercial Bank',
  'Bangkok Bank': 'Bangkok Bank',
  'Krung Thai Bank': 'Krung Thai Bank',
  'TMB Bank': 'TMB Bank',
  'Bank of Ayudhya': 'Bank of Ayudhya',
  'Thanachart Bank': 'Thanachart Bank',
  'Kiatnakin Bank': 'Kiatnakin Bank',
  'CIMB Thai': 'CIMB Thai',
  'Government Saving Bank': 'Government Saving Bank',
  'UOB(Thai)': 'UOB(Thai)',
  'Tisco Bank': 'Tisco Bank',
  'Standard Chartered (Thailand) Bank': 'Standard Chartered (Thailand) Bank',
  'Government Housing Bank': 'Government Housing Bank',
  'Bank for Agriculture and Agricultural Co-operatives': 'Bank for Agriculture and Agricultural Co-operatives',
  'ICBC(Thai)': 'ICBC (Thai)',
  'Thai Credit Retail Bank': 'Thai Credit Retail Bank',
  'LH Bank Plc': 'LH Bank Plc',
  'Citi Bank': 'Citi Bank',
  'Mizuho Bank': 'Mizuho Bank',
  'Islam Bank': 'Islam Bank',
  'Sumitomo Mitsui': 'Sumitomo Mitsui',
  HSBC: 'HSBC',
  'TMBThanachart Bank Public Company Limited': 'TMBThanachart Bank Public Company Limited',
};

export const BANK_LIST_TH: Record<string, string> = {
  Kasikornbank: 'ธนาคาร กสิกรไทย',
  'Siam Commercial Bank': 'ธนาคาร ไทยพาณิชย์',
  'Bangkok Bank': 'ธนาคาร กรุงเทพ',
  'Krung Thai Bank': 'ธนาคาร กรุงไทย',
  'TMB Bank': 'ธนาคาร ทหารไทย',
  'Bank of Ayudhya': 'ธนาคาร กรุงศรีอยุธยา',
  'Thanachart Bank': 'ธนาคาร ธนชาต',
  'Kiatnakin Bank': 'ธนาคาร เกียรตินาคิน',
  'CIMB Thai': 'ธนาคาร ซีไอเอ็มบีไทย',
  'Government Saving Bank': 'ธนาคาร ออมสิน',
  'UOB(Thai)': 'ธนาคาร ยูโอบี (ไทย)',
  'Tisco Bank': 'ธนาคาร ทิสโก้',
  'Standard Chartered (Thailand) Bank': 'ธนาคาร สแตนดาร์ดชาร์เตอร์ด (ไทย)',
  'Government Housing Bank': 'ธนาคาร อาคารสงเคราะห์',
  'Bank for Agriculture and Agricultural Co-operatives': 'ธนาคาร เพื่อการเกษตรและสหกรณ์การเกษตร',
  'ICBC(Thai)': 'ธนาคาร ไอซีบีซี (ไทย)',
  'Thai Credit Retail Bank': 'ธนาคาร ไทยเครดิต เพื่อรายย่อย',
  'LH Bank Plc': 'ธนาคาร แลนด์ แอนด์ เฮ้าส์',
  'Citi Bank': 'ธนาคาร ซิตี้แบงก์',
  'Mizuho Bank': 'ธนาคาร มิซูโฮ',
  'Islam Bank': 'ธนาคาร อิสลาม',
  'Sumitomo Mitsui': 'ธนาคาร ซูมิโตโมมิตซุย',
  HSBC: 'ธนาคาร เอชเอสบีซี',
  'TMBThanachart Bank Public Company Limited': 'ธนาคารทหารไทยธนชาต',
};

export interface BankOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export const BANK_OPTIONS: BankOption[] = Object.keys(BANK_LIST_EN).map((name) => ({
  value: name,
  label: BANK_LIST_EN[name],
}));

/**
 * An unregistered car has no plate number yet, so one of these words stands in for one.
 *
 * Two constants, not one, and the split is load-bearing: the base plate rule exempts the exact word
 * `RED_LICENSE_PLATE` and nothing else, while the insurer-specific rules honour the whole
 * `RED_VEHICLE_NUMBERS` list. That is legacy behaviour and is why both exist.
 */
export const RED_LICENSE_PLATE = 'ป้ายแดง';

export const RED_VEHICLE_NUMBERS: readonly string[] = [RED_LICENSE_PLATE, 'ปด', 'ป้ายแดง-', 'ปด-', 'ใหม่'];

/**
 * The reasons an operator can give for cancelling a policy, as the backend stores them on
 * `cancellation_reason` (the stored value is the English sentence, hence `value`).
 *
 * Canonical here, not in a feature module: the policy-in-progress cancellation form writes them,
 * the endorsement detail page reads them back, and the ops dashboard's request card renders them.
 * Three surfaces on one list — a second copy would drift the moment the list changed.
 */
export const CANCELLATION_REASONS = [
  {
    value: 'Incomplete information — unable to contact customer to verify details',
    labelKey: 'endorsementCancellationReasonIncompleteInfo',
  },
  {
    value: 'Unable to schedule vehicle inspection or confirm damage/injury with customer',
    labelKey: 'endorsementCancellationReasonVehicleInspection',
  },
  { value: 'Insurer rejected the sale reporting', labelKey: 'endorsementCancellationReasonInsurerRejected' },
  { value: 'Customer payment default / Overdue payment', labelKey: 'endorsementCancellationReasonPaymentDefault' },
  { value: 'Customer requests policy cancellation', labelKey: 'endorsementCancellationReasonCustomerRequest' },
] as const;
